import { useNavigate, useParams } from '@solidjs/router'
import { type JSX, Show, createComputed, createEffect, onMount } from 'solid-js'
import { fetchUserPreferences } from '../../api/endpoints/preferences.api'
import { fetchWorkspace } from '../../api/endpoints/workspace.api'
import type { Candidate, UserType } from '../../api/types/generated'
import { activationStore } from '../../stores/activation.store'
import { authStore } from '../../stores/auth.store'
import {
  getUserContext,
  initializeForCandidate,
  initializeForHiringManager,
  isPreferencesLoaded,
  setUserContextEagerly,
} from '../../stores/preferences.store'
import { workspaceStore } from '../../stores/workspace.store'
import { ensureSession } from '../session/ensureSession'
import {
  fallbackLoginForPath,
  homePathFor,
  loginPathFor,
  storeReturnPath,
} from '../utils/authRoutes'
import { DeletionFreezeGate } from './DeletionFreezeGate'
import { PendingApprovalGate } from './PendingApprovalGate'
import { RouteSkeleton } from './RouteSkeleton'

interface ProtectedRouteProps {
  children: JSX.Element
  requiredUserType?: UserType | undefined
  fallbackPath?: string
}

export function ProtectedRoute(props: ProtectedRouteProps) {
  const navigate = useNavigate()
  const params = useParams<{ workspaceSlug?: string }>()
  const fallbackPath = (userType: UserType | null, requiredUserType: UserType) =>
    props.fallbackPath || (userType ? homePathFor(userType) : loginPathFor(requiredUserType))

  createComputed(() => {
    if (params.workspaceSlug) {
      workspaceStore.switchWorkspace(params.workspaceSlug)
    }
  })

  onMount(() => {
    void import('./AppShell')
    ensureSession({ requiredUserType: props.requiredUserType })
  })

  createEffect(() => {
    if (
      !authStore.state.isLoading &&
      authStore.state.isAuthenticated &&
      authStore.state.userType === 'hiring_manager' &&
      authStore.isHMApproved &&
      getUserContext() !== 'hiring_manager'
    ) {
      setUserContextEagerly('hiring_manager')

      Promise.all([fetchWorkspace(), fetchUserPreferences()])
        .then(async ([workspace, userPrefs]) => {
          workspaceStore.setWorkspaceData(workspace)

          void workspaceStore.fetchUsage()

          await initializeForHiringManager(
            workspace != null,
            workspace?.audio_preferences,
            userPrefs
          )
        })
        .catch((err) => {
          console.warn('Failed to initialize HM preferences', err)
          initializeForHiringManager(false, null, null)
        })
    }
  })

  createEffect(() => {
    if (
      !authStore.state.isLoading &&
      authStore.state.isAuthenticated &&
      authStore.state.userType === 'candidate' &&
      getUserContext() !== 'candidate'
    ) {
      const candidate = authStore.state.user as Candidate | null
      initializeForCandidate(
        null,
        undefined,
        candidate?.language
          ? { language: candidate.language, unconfirmed: candidate.language_unconfirmed === true }
          : undefined
      )
      void activationStore.fetch()
    }
  })

  createEffect(() => {
    if (!authStore.state.isLoading && !authStore.state.isAuthenticated) {
      storeReturnPath(window.location.pathname + window.location.search)
      if (props.requiredUserType) {
        navigate(loginPathFor(props.requiredUserType))
      } else {
        navigate(fallbackLoginForPath(window.location.pathname))
      }
    }

    const requiredUserType = props.requiredUserType
    const userType = authStore.state.userType
    if (
      !authStore.state.isLoading &&
      authStore.state.isAuthenticated &&
      requiredUserType &&
      userType !== requiredUserType
    ) {
      navigate(fallbackPath(userType, requiredUserType), { replace: true })
    }
  })

  const isReady = () => {
    if (!authStore.state.isAuthenticated || authStore.state.isLoading) {
      return false
    }
    if (props.requiredUserType && authStore.state.userType !== props.requiredUserType) {
      return false
    }
    if (authStore.state.userType === 'hiring_manager') {
      if (!authStore.isHMApproved) {
        return true
      }
      return isPreferencesLoaded()
    }
    return true
  }

  return (
    <Show when={isReady()} fallback={<RouteSkeleton />}>
      <Show
        when={authStore.state.userType === 'candidate'}
        fallback={<PendingApprovalGate>{props.children}</PendingApprovalGate>}
      >
        <DeletionFreezeGate>{props.children}</DeletionFreezeGate>
      </Show>
    </Show>
  )
}
